/**
 * @fileoverview Registration form component for Essential Eight assessment
 * Collects user details and submits to HubSpot before allowing access to the assessment
 */

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { submitLeadToHubspot } from '@/services/hubspotService';

interface RegistrationFormProps {
  open: boolean;
  onComplete: () => void;
  onClose: () => void;
}

/**
 * Registration form component for collecting user details
 * before allowing access to the Essential Eight assessment
 */
const RegistrationForm: React.FC<RegistrationFormProps> = ({ open, onComplete, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    jobTitle: '',
    phone: '',
    marketingConsent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleCheckboxChange = (checked: boolean) => {
    setFormData({
      ...formData,
      marketingConsent: checked
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    if (!formData.email) {
      setError('Email is required');
      setIsSubmitting(false);
      return;
    }

    if (!formData.marketingConsent) {
      setError('You must agree to receive marketing emails to continue');
      setIsSubmitting(false);
      return;
    }

    try {
      // Always store data locally first for PDF generation
      sessionStorage.setItem('e8-lead-email', formData.email);
      sessionStorage.setItem('e8-lead-company', formData.company || '');
      
      const success = await submitLeadToHubspot(formData);
      
      if (success) {
        onComplete();
      } else {
        setError('Failed to register. Please try again later.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      // Only allow closing through the explicit buttons, not by clicking outside
      // or pressing escape key
      return;
    }}>
      <DialogContent className="sm:max-w-[425px] bg-card">
        <DialogHeader>
          <DialogTitle>Register for Essential Eight Assessment</DialogTitle>
          <DialogDescription>
            Enter your details to access the Essential Eight maturity assessment tool.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="company">Company</Label>
              <Input
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
              />
            </div>
            
            {/* <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="jobTitle">Job Title</Label>
                <Input
                  id="jobTitle"
                  name="jobTitle"
                  value={formData.jobTitle}
                  onChange={handleChange}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
            </div> */}

            <div className="flex items-center space-x-2 pt-2">
              <Checkbox 
                id="marketingConsent" 
                name="marketingConsent"
                checked={formData.marketingConsent}
                onCheckedChange={handleCheckboxChange}
                required
              />
              <Label 
                htmlFor="marketingConsent" 
                className="text-sm font-normal leading-tight"
              >
                <span className="cursor-pointer" onClick={(e) => {
                  e.stopPropagation();
                  window.open('https://deiterate.com/privacy-policy/', '_blank', 'noopener,noreferrer');
                }}>
                  I agree to receive communications from de.iterate. You can unsubscribe at any time. View our Privacy Policy.
                </span>
              </Label>
            </div>
            
            {error && <div className="text-red-500 text-sm">{error}</div>}
          </div>
          
          <DialogFooter>
            {/* Cancel button removed to make registration mandatory */}
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Registering...' : 'Start Assessment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RegistrationForm;
